// Renders a single CMS block based on its type.
// Keep this purely presentational so the same component works in the admin preview and on public pages.
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

function isExternal(href = "") {
  return /^(https?:|mailto:|tel:|#)/i.test(href);
}

function CTAButton({ label, href, variant = "primary", testId }) {
  if (!label) return null;
  const cls = variant === "primary" ? "btn-primary" : "btn-secondary";
  if (isExternal(href || "")) {
    return <a data-testid={testId} href={href || "#"} className={`${cls} inline-flex items-center gap-2`}>{label}{variant === "primary" && <ArrowRight size={16} weight="bold" />}</a>;
  }
  return <Link data-testid={testId} to={href || "/"} className={`${cls} inline-flex items-center gap-2`}>{label}{variant === "primary" && <ArrowRight size={16} weight="bold" />}</Link>;
}

export default function BlockRenderer({ block }) {
  if (!block || !block.type) return null;
  const d = block.data || {};
  switch (block.type) {
    case "hero":
      return (
        <section className="bg-gradient-to-br from-[#1b4a76] to-[#102e4a] text-white" data-testid={`block-hero-${block.id}`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
            {d.eyebrow && <p className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-3">{d.eyebrow}</p>}
            {d.headline && <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 max-w-3xl">{d.headline}</h2>}
            {d.subhead && <p className="text-lg opacity-90 leading-relaxed max-w-2xl mb-6">{d.subhead}</p>}
            <div className="flex flex-wrap gap-3">
              <CTAButton label={d.primary_cta_label} href={d.primary_cta_href} variant="primary" testId={`block-${block.id}-cta1`} />
              <CTAButton label={d.secondary_cta_label} href={d.secondary_cta_href} variant="secondary" testId={`block-${block.id}-cta2`} />
            </div>
          </div>
        </section>
      );

    case "text":
      return (
        <section className="bg-white" data-testid={`block-text-${block.id}`}>
          <div className="max-w-3xl mx-auto px-6 lg:px-12 py-14">
            {d.heading && <h2 className="font-heading text-3xl font-bold text-stone-900 mb-4">{d.heading}</h2>}
            {d.body && <div className="prose prose-stone max-w-none text-stone-700 leading-relaxed whitespace-pre-line">{d.body}</div>}
          </div>
        </section>
      );

    case "image":
      return (
        <section className="bg-[#FDFBF7]" data-testid={`block-image-${block.id}`}>
          <div className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
            {d.src && <img src={d.src} alt={d.alt || ""} className="w-full rounded-2xl shadow-md" />}
            {d.caption && <p className="text-sm text-stone-500 text-center mt-3 italic">{d.caption}</p>}
          </div>
        </section>
      );

    case "cta":
      return (
        <section className="bg-stone-100" data-testid={`block-cta-${block.id}`}>
          <div className="max-w-4xl mx-auto px-6 lg:px-12 py-14 text-center">
            {d.heading && <h2 className="font-heading text-3xl font-bold text-stone-900 mb-3">{d.heading}</h2>}
            {d.body && <p className="text-stone-600 mb-6 max-w-2xl mx-auto">{d.body}</p>}
            <CTAButton label={d.button_label} href={d.button_href} variant="primary" testId={`block-${block.id}-cta`} />
          </div>
        </section>
      );

    case "faq_list":
      return (
        <section className="bg-white" data-testid={`block-faq-${block.id}`}>
          <div className="max-w-3xl mx-auto px-6 lg:px-12 py-14">
            {d.heading && <h2 className="font-heading text-3xl font-bold text-stone-900 mb-6">{d.heading}</h2>}
            <div className="space-y-3">
              {(d.items || []).map((f, i) => (
                <details key={i} className="card-pp p-5 group">
                  <summary className="cursor-pointer font-heading font-semibold text-stone-900 list-none flex justify-between items-center">
                    {f.q}
                    <span className="text-[#f38838] group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <p className="text-stone-600 mt-3 text-sm leading-relaxed whitespace-pre-line">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      );

    default:
      // Hide unknown types from end-users — return nothing instead of leaking the type name.
      return null;
  }
}

// Default empty data per block type — used when admin adds a new block.
export const BLOCK_DEFAULTS = {
  hero: { eyebrow: "", headline: "New section heading", subhead: "Write a short supporting sentence here.", primary_cta_label: "", primary_cta_href: "", secondary_cta_label: "", secondary_cta_href: "" },
  text: { heading: "Section heading", body: "Write your content here." },
  image: { src: "", alt: "", caption: "" },
  cta: { heading: "Ready to get started?", body: "A short call to action sub-line.", button_label: "Get in touch", button_href: "/contact" },
  faq_list: { heading: "Frequently asked", items: [{ q: "Sample question?", a: "Sample answer." }] },
};

export const BLOCK_LABELS = {
  hero: "Hero (eyebrow + headline + CTAs)",
  text: "Text section (heading + body)",
  image: "Image with caption",
  cta: "Call-to-action banner",
  faq_list: "FAQ list",
};
